import { useEffect, useRef, useState } from 'react'
import type { TranslationKey } from '@/modules/i18n'

export type LogoVariant = 'wordmark' | 'square'

export type ValidationStatus = 'idle' | 'loading' | 'ok' | 'warn' | 'error'

export interface ValidationResult {
  status: ValidationStatus
  messages: TranslationKey[]
}

const DEBOUNCE_MS = 500

// Aspect ratio bounds (width / height).
const WORDMARK_MIN_RATIO = 2.0
const WORDMARK_MAX_RATIO = 4.0
const SQUARE_MIN_RATIO = 0.95
const SQUARE_MAX_RATIO = 1.05

// File size caps in bytes.
const WORDMARK_MAX_BYTES = 300 * 1024
const SQUARE_MAX_BYTES = 150 * 1024

const IDLE_RESULT: ValidationResult = { status: 'idle', messages: [] }
const LOADING_RESULT: ValidationResult = { status: 'loading', messages: [] }

const isEmpty = (url: string | undefined | null): boolean =>
  url === null || url === undefined || url.trim() === ''

/**
 * Validates a logo URL for the OrganizationForm — returns translation keys, not
 * rendered text (the caller is responsible for translating). Validation runs
 * sequentially, debounced 500ms, and is cancellable on URL change / unmount.
 *
 * Errors block save (HTTPS scheme, image load). Aspect ratio + file size are
 * warn-only. Missing Content-Length / HEAD failure is silently skipped.
 */
export function useLogoUrlValidation(
  url: string | undefined | null,
  expected: LogoVariant,
): ValidationResult {
  const [result, setResult] = useState<ValidationResult>(() =>
    isEmpty(url) ? IDLE_RESULT : LOADING_RESULT,
  )

  // Track every active resource per effect run so we can cancel on rerun.
  const activeImageRef = useRef<HTMLImageElement | null>(null)
  const activeAbortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (isEmpty(url)) {
      setResult(IDLE_RESULT)
      return
    }

    // Sentinel — set false on cleanup to discard any late-arriving callbacks.
    let cancelled = false
    setResult(LOADING_RESULT)

    const debounceTimer = setTimeout(() => {
      if (cancelled) return

      const trimmed = (url as string).trim()

      // Step 2: HTTPS gate.
      if (!trimmed.startsWith('https://')) {
        setResult({
          status: 'error',
          messages: ['common.logoURLErrorNotHttps'],
        })
        return
      }

      const warnings: TranslationKey[] = []
      const errors: TranslationKey[] = []

      // Step 5 (parallel with image load): HEAD the URL for Content-Length.
      const abort = new AbortController()
      activeAbortRef.current = abort

      const sizePromise: Promise<void> = fetch(trimmed, {
        method: 'HEAD',
        signal: abort.signal,
      })
        .then((response) => {
          if (cancelled || !response.ok) return
          const lengthHeader = response.headers.get('Content-Length')
          if (!lengthHeader || lengthHeader.trim() === '') return
          const bytes = Number(lengthHeader)
          if (!Number.isFinite(bytes) || bytes <= 0) return
          const cap =
            expected === 'wordmark' ? WORDMARK_MAX_BYTES : SQUARE_MAX_BYTES
          if (bytes > cap) {
            warnings.push('common.logoURLWarnTooLarge')
          }
        })
        .catch(() => {
          // Network error / CORS / abort — silently skip, per spec.
        })

      // Step 3 + 4: image load + aspect ratio.
      const image = new Image()
      activeImageRef.current = image

      const imagePromise = new Promise<void>((resolve) => {
        image.onload = () => {
          if (cancelled) {
            resolve()
            return
          }
          const w = image.naturalWidth || image.width
          const h = image.naturalHeight || image.height
          if (w > 0 && h > 0) {
            const ratio = w / h
            if (expected === 'wordmark') {
              if (ratio < WORDMARK_MIN_RATIO || ratio > WORDMARK_MAX_RATIO) {
                warnings.push('common.logoURLWarnAspectWordmark')
              }
            } else {
              if (ratio < SQUARE_MIN_RATIO || ratio > SQUARE_MAX_RATIO) {
                warnings.push('common.logoURLWarnAspectSquare')
              }
            }
          }
          resolve()
        }
        image.onerror = () => {
          if (cancelled) {
            resolve()
            return
          }
          errors.push('common.logoURLErrorFailedToLoad')
          resolve()
        }
      })

      image.src = trimmed

      Promise.all([imagePromise, sizePromise]).then(() => {
        if (cancelled) return
        if (errors.length > 0) {
          setResult({ status: 'error', messages: errors })
          return
        }
        if (warnings.length > 0) {
          setResult({ status: 'warn', messages: warnings })
          return
        }
        setResult({ status: 'ok', messages: [] })
      })
    }, DEBOUNCE_MS)

    return () => {
      cancelled = true
      clearTimeout(debounceTimer)
      const img = activeImageRef.current
      if (img) {
        img.onload = null
        img.onerror = null
        // Detach src so the browser stops fetching.
        try {
          img.src = ''
        } catch {
          // Ignore — some environments throw on empty src.
        }
        activeImageRef.current = null
      }
      const abort = activeAbortRef.current
      if (abort) {
        abort.abort()
        activeAbortRef.current = null
      }
    }
  }, [url, expected])

  return result
}

export default useLogoUrlValidation
